// Settings → Website Integration.
//
// The embeddable AI sales chat the retailer drops onto their own website with
// one line. Backed by:
//   GET /api/portal/settings/website-chat → { enabled, greeting, slug, embedSnippet, wizardUrl }
//   PUT /api/portal/settings/website-chat   save { enabled, greeting }

import { iconMarkup } from "@tkos/shared-ui";

import type { PageContext } from "../../../shell/types";
import type { SettingsTab, TabCleanup } from "../types";

import "./website-integration.css";

interface WcSettings {
  ok?: boolean;
  enabled?: boolean;
  greeting?: string;
  slug?: string;
  embedSnippet?: string;
  wizardUrl?: string;
}

function previewUrlFrom(snippet: string, slug: string): string {
  const m = snippet.match(/src="([^"]+)\/embed\/chat\.js"/);
  const base = m ? m[1] : "";
  return base ? `${base}/embed/chat-frame?retailer=${encodeURIComponent(slug)}&preview=1` : "";
}

const websiteIntegrationTab: SettingsTab = {
  id: "website-integration",
  label: "Website Integration",
  icon: iconMarkup("chat", { size: "sm" }),
  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    const ac = new AbortController();
    let snippet = "";
    let wizardUrl = "";
    let slug = "";

    const root = document.createElement("div");
    root.className = "set-wc-root";
    root.innerHTML = `
      <p class="set-wc-intro">
        Add your branded AI sales assistant to your own website. It chats with visitors, qualifies them
        into leads, and hands them straight into your lead-gen wizard — every conversation lands in your
        CRM. Paste this one line into your site's HTML, just before <code>&lt;/body&gt;</code>.
      </p>
      <div class="set-wc-card">
        <label class="set-wc-toggle">
          <input type="checkbox" data-role="enabled" />
          <span>Enabled — show the chat on my website</span>
        </label>

        <div class="set-wc-field">
          <span class="set-wc-label">Embed code</span>
          <div class="set-wc-snippet-row">
            <textarea class="set-wc-snippet" data-role="snippet" rows="2" readonly></textarea>
            <button type="button" class="set-btn" data-role="copy">Copy</button>
          </div>
          <span class="set-wc-hint">Paste once. Updates to your branding and assistant name apply automatically.</span>
        </div>

        <label class="set-wc-field">
          <span class="set-wc-label">Custom greeting <em>optional</em></span>
          <textarea class="set-wc-input" data-role="greeting" rows="2" maxlength="2000"
                    placeholder="e.g. Hi! Thinking about a new kitchen? I can help you get started."></textarea>
          <span class="set-wc-hint">The assistant's opening line on your website. Leave blank for a friendly default.</span>
        </label>

        <div class="set-wc-field">
          <span class="set-wc-label">Wizard URL</span>
          <input type="text" class="set-wc-input" data-role="wizard" readonly />
          <span class="set-wc-hint">Where qualified visitors are handed off. Auto-filled from your account.</span>
        </div>

        <div class="set-wc-footer">
          <button type="button" class="set-btn set-btn--ghost" data-role="preview">Preview</button>
          <span class="set-wc-status" data-role="status"></span>
          <button type="button" class="set-btn set-btn--primary" data-role="save">Save</button>
        </div>
      </div>`;
    container.appendChild(root);

    const enabledEl = root.querySelector<HTMLInputElement>("[data-role='enabled']")!;
    const snippetEl = root.querySelector<HTMLTextAreaElement>("[data-role='snippet']")!;
    const greetingEl = root.querySelector<HTMLTextAreaElement>("[data-role='greeting']")!;
    const wizardEl = root.querySelector<HTMLInputElement>("[data-role='wizard']")!;
    const statusEl = root.querySelector<HTMLElement>("[data-role='status']")!;
    const copyBtn = root.querySelector<HTMLButtonElement>("[data-role='copy']")!;
    const saveBtn = root.querySelector<HTMLButtonElement>("[data-role='save']")!;
    const previewBtn = root.querySelector<HTMLButtonElement>("[data-role='preview']")!;

    const setStatus = (msg: string, kind: "" | "saved" | "error" = ""): void => {
      statusEl.textContent = msg;
      statusEl.className = `set-wc-status${kind ? ` set-wc-status--${kind}` : ""}`;
    };

    const load = async (): Promise<void> => {
      setStatus("Loading…");
      try {
        const d = await ctx.api<WcSettings>("/portal/settings/website-chat", { signal: ac.signal });
        if (ac.signal.aborted) return;
        enabledEl.checked = d.enabled === true;
        greetingEl.value = d.greeting ?? "";
        snippet = d.embedSnippet ?? "";
        wizardUrl = d.wizardUrl ?? "";
        slug = d.slug ?? "";
        snippetEl.value = snippet;
        wizardEl.value = wizardUrl;
        setStatus("");
      } catch (err) {
        if ((err as { name?: string }).name === "AbortError") return;
        setStatus("Couldn't load settings.", "error");
      }
    };

    const save = async (): Promise<void> => {
      setStatus("Saving…");
      try {
        await ctx.api("/portal/settings/website-chat", {
          method: "PUT",
          body: JSON.stringify({ enabled: enabledEl.checked, greeting: greetingEl.value.trim() }),
          signal: ac.signal,
        });
        setStatus("Saved.", "saved");
      } catch (err) {
        if ((err as { name?: string }).name === "AbortError") return;
        setStatus(err instanceof Error ? err.message : "Save failed.", "error");
      }
    };

    copyBtn.addEventListener("click", () => {
      if (!snippet) return;
      void navigator.clipboard?.writeText(snippet).then(
        () => { copyBtn.textContent = "Copied!"; window.setTimeout(() => { copyBtn.textContent = "Copy"; }, 1500); },
        () => { snippetEl.select(); },
      );
    });
    previewBtn.addEventListener("click", () => {
      const url = previewUrlFrom(snippet, slug);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else ctx.showToast("Preview unavailable — save your settings first.", "info");
    });
    saveBtn.addEventListener("click", () => void save());

    void load();

    return () => { ac.abort(); root.remove(); };
  },
};

export default websiteIntegrationTab;
